// Pure reconcile math + validation for the trip close-out sheet (M67b).
//
// These rules MIRROR the reconcile_manifest_line RPC's RAISE cases one-to-one
// (migration 20260711000000) so the sheet can disable Save with a friendly
// message before the DB ever rejects. The RPC stays the authority; this is the
// pre-flight. If the RPC's guards change, the parity tests here must change too.

import { configurationLabel, firearmLabel } from './configurationLabel';
import type { Firearm, FirearmBuild } from './types';

export interface ReconcileInput {
  packed: number;
  returned: number;
  lost: number;
  /** lot's current on-hand at reconcile time (inventory_ammo.quantity). */
  onHand: number;
}

export interface ReconcileValidation {
  ok: boolean;
  /** user-facing message when !ok; empty when ok. */
  error: string;
  /** packed − returned − lost (may be shown even when !ok, for the live figure). */
  fired: number;
}

export function computeFired(packed: number, returned: number, lost: number): number {
  return packed - returned - lost;
}

export function validateReconcileLine(input: ReconcileInput): ReconcileValidation {
  const { packed, returned, lost, onHand } = input;
  const fired = computeFired(packed, returned, lost);

  if (returned < 0 || lost < 0) {
    return { ok: false, error: 'Brought back and lost counts must be zero or more.', fired };
  }
  if (returned + lost > packed) {
    return {
      ok: false,
      error: `Brought back (${returned}) plus lost (${lost}) can't exceed the ${packed} rounds packed.`,
      fired,
    };
  }
  // The trip deducts fired + lost = packed − returned. Guard against over-draw
  // (other consumption may have intervened since packing).
  const deduct = packed - returned;
  if (deduct > onHand) {
    return {
      ok: false,
      error: `Only ${onHand.toLocaleString()} rounds on hand in this lot — closing out would deduct ${deduct.toLocaleString()}. Adjust brought back or lost.`,
      fired,
    };
  }
  return { ok: true, error: '', fired };
}

/**
 * Integer-safe even split of `fired` across `n` attribution rows. Remainder
 * lands on the first row so no round is lost to rounding. Mirrors the RPC's
 * default split (rows ordered created_at, id).
 */
export function evenSplit(fired: number, n: number): number[] {
  if (n <= 0) return [];
  const base = Math.floor(fired / n);
  const rem = fired - base * n;
  return Array.from({ length: n }, (_, i) => base + (i === 0 ? rem : 0));
}

/** Does closing out drop this lot below its low-stock threshold? (C3.1) */
export function crossesLowStock(newQty: number, threshold: number): boolean {
  return threshold > 0 && newQty < threshold;
}

/** One attribution row's committed share of a lot's fired rounds. */
export interface AttributionShare {
  id: string;
  roundsUsed: number;
}

/**
 * The per-firearm split to replay when restoring an undone line (M77a, review
 * OV4), or null when the server's own default would reproduce it.
 *
 * Undo zeroes every `rounds_used` for the lot, so a multi-gun line's
 * attribution — which rounds went through which firearm — exists nowhere else
 * once the undo lands. Capturing it BEFORE the undo is what lets Restore put
 * back the same trip rather than an evenly-smeared approximation.
 *
 * Returns null when the stored shares already equal `evenSplit`, so those lines
 * replay as a plain 3-argument call and the RPC applies its own default. Same
 * outcome, one less thing asserted — and it keeps the 3-arg call shape that the
 * close-out sheet's tests pin.
 *
 * Rows are sorted by id ascending to match the RPC (remainder → lowest id) and
 * `evenSplit` (remainder → element 0); comparing in any other order would call
 * an even split uneven.
 */
export function restoreSplit(
  rows: AttributionShare[],
  fired: number,
): Record<string, number> | null {
  if (rows.length < 2) return null;

  const ordered = [...rows].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const even = evenSplit(fired, ordered.length);
  if (ordered.every((r, i) => r.roundsUsed === even[i])) return null;

  const out: Record<string, number> = {};
  for (const r of ordered) out[r.id] = r.roundsUsed;
  return out;
}

// ---------------------------------------------------------------------------
// M77e — the per-gun split
// ---------------------------------------------------------------------------

export interface AttributionRow {
  id: string;
  firearm_id: string | null;
  build_id: string | null;
}

export interface SplitRow {
  /** range_session_ammo.id — the key the RPC's p_split map is keyed by. */
  id: string;
  label: string;
}

/**
 * One attribution row's display label. Extracted from the trip-line chip logic
 * so the split editor and the chips can never disagree about what a row is
 * called — the split inputs ARE those chips, asked to hold a number.
 *
 * A build wins over its firearm because it is the more specific thing the user
 * named. An unattributed row is labeled, not hidden (DD9): it still receives
 * rounds, and a blank label would make the editor look broken.
 */
export function attributionLabel(
  row: Pick<AttributionRow, 'firearm_id' | 'build_id'>,
  firearms: Pick<Firearm, 'id' | 'make' | 'model' | 'caliber'>[],
  /** M97a (R12, E2) — ALSO the collision pool: pass the page's full builds
   *  query so this chip suffixes a build's name exactly when the pickers and
   *  the packing list do. A partial pool would name one row two ways. */
  builds: Pick<FirearmBuild, 'id' | 'name' | 'firearm_id' | 'is_active'>[],
): string {
  // M82d — this used to return the BUILD NAME alone for an attributed row
  // ("Factory"), while the picker beside it said "Glock 19 — Factory" and the
  // session row said "Glock 19". Three names for one thing. The M77e invariant
  // is "one name, never disagree", and this is the same function every other
  // surface calls. M97a — one line, so it renders the one-line `label`.
  const build = row.build_id ? builds.find((b) => b.id === row.build_id) : null;
  const f = firearms.find((x) => x.id === row.firearm_id);
  // An attributed row whose firearm is missing from the pool still has a name
  // worth showing — preserved from the pre-M82d behaviour.
  if (!f) return build ? build.name : 'Not attributed';
  return configurationLabel(
    { label: firearmLabel(f), caliber: f.caliber },
    build ?? null,
    builds,
  ).label;
}

/**
 * M82d (D24) — which of THIS session's rows follow when a firearm's
 * configuration is changed.
 *
 * THE RULE IS EXACT-MATCH, and the two rows it deliberately leaves behind are
 * the reason this is a function rather than an `update ... where firearm_id`:
 *
 *   - a row attributed to a DIFFERENT build of the same gun stays. The owner
 *     said that ammo went through that configuration; changing the session's
 *     primary configuration is not a statement about it.
 *   - a NULL-build legacy row stays NULL. Which configuration it was is
 *     genuinely unknowable, and a value written here would be indistinguishable
 *     from a fact downstream.
 *
 * ONE function, two callers: the snackbar counts what this returns, and the
 * write mutates exactly what this returns. That is the E9.3/M77c
 * preview-equals-commit rule — a count computed separately from the write is a
 * count that can lie.
 */
export interface RippleRow {
  id: string;
  firearm_id: string | null;
  build_id: string | null;
}

export interface ConfigurationRipple {
  ammoIds: string[];
  magazineIds: string[];
  /** What the snackbar says. Always ammoIds.length + magazineIds.length. */
  count: number;
}

export function configurationRipple(
  ammo: RippleRow[],
  magazines: RippleRow[],
  firearmId: string,
  oldBuildId: string | null,
): ConfigurationRipple {
  // A legacy row carries no build to move FROM, so nothing follows it.
  const follows = (r: RippleRow) =>
    oldBuildId !== null && r.firearm_id === firearmId && r.build_id === oldBuildId;
  const ammoIds = ammo.filter(follows).map((r) => r.id);
  const magazineIds = magazines.filter(follows).map((r) => r.id);
  return { ammoIds, magazineIds, count: ammoIds.length + magazineIds.length };
}

/** The snackbar sentence, from the same result the write uses. */
export function rippleMessage(r: ConfigurationRipple): string {
  if (r.count === 0) return 'Configuration changed';
  const noun = r.count === 1 ? 'line' : 'lines';
  return `Configuration changed · ${r.count} ${noun} re-attributed`;
}

/**
 * The split editor's rows, ORDERED BY id ASCENDING.
 *
 * That order is not cosmetic. `reconcile_manifest_line` builds its default
 * split from `array_agg(id ORDER BY id)` and puts the odd remainder on the
 * FIRST element; `evenSplit` puts the remainder on element 0. Ordering the UI
 * any other way would show one row a number the server would have given to a
 * different one, so an untouched line's prefill and the server's own default
 * would silently disagree by the remainder.
 */
export function splitRows(
  rows: AttributionRow[],
  firearms: Pick<Firearm, 'id' | 'make' | 'model' | 'caliber'>[],
  builds: Pick<FirearmBuild, 'id' | 'name' | 'firearm_id' | 'is_active'>[],
): SplitRow[] {
  return [...rows]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((r) => ({ id: r.id, label: attributionLabel(r, firearms, builds) }));
}

/**
 * DD9 — "firearms" is THE noun, except when the set isn't all firearms.
 * A lot fired through two guns and one unattributed row is three ENTRIES, and
 * calling those "3 firearms" would be a small, checkable lie.
 */
export function splitNoun(rows: SplitRow[]): string {
  return rows.some((r) => r.label === 'Not attributed') ? 'entries' : 'firearms';
}

export interface SplitValidation {
  ok: boolean;
  error: string;
  /** What the typed rows currently add up to. */
  sum: number;
}

/**
 * Mirrors `reconcile_manifest_line`'s split RAISE one-to-one. This one belongs
 * in this file rather than magCloseOut: it IS a real RPC guard, and the mirror
 * is the contract this module exists to keep.
 */
export function validateSplit(values: Record<string, number>, fired: number): SplitValidation {
  const entries = Object.values(values);
  if (entries.some((n) => !Number.isInteger(n) || n < 0)) {
    return { ok: false, error: 'Each firearm’s rounds must be a whole number, zero or more.', sum: 0 };
  }
  const sum = entries.reduce((s, n) => s + n, 0);
  if (sum !== fired) {
    return { ok: false, error: `splits ${sum.toLocaleString()} of ${fired.toLocaleString()} fired`, sum };
  }
  return { ok: true, error: '', sum };
}

/**
 * The prefill: an even split, keyed by row id in the same order the server
 * would use. Recomputed as `fired` changes until the user touches a row —
 * after that their numbers are the intent, and silently re-spreading them
 * would discard it.
 */
export function evenSplitMap(rows: SplitRow[], fired: number): Record<string, number> {
  const shares = evenSplit(fired, rows.length);
  const out: Record<string, number> = {};
  rows.forEach((r, i) => { out[r.id] = shares[i] ?? 0; });
  return out;
}

/**
 * #400 — the ammo-side attribution default, mirroring `defaultMagAttribution`.
 *
 * Gap #7 had a third cause the plan never named: nobody attributes. The picker
 * starts empty and is easy to skip, so every gun count stayed at zero even
 * after both real bugs were fixed. When the trip has exactly ONE firearm there
 * is nothing to guess at, so defaulting costs the user nothing and is almost
 * always what they meant.
 *
 * A BUILD is never defaulted, for the same reason D3 gives on the magazine
 * side: a wrong build writes a lifetime number nobody thinks to audit.
 */
export function defaultAmmoAttribution(
  sessionFirearmIds: (string | null)[],
): { firearmId: string; buildId: null } | null {
  const ids = [...new Set(sessionFirearmIds.filter((id): id is string => !!id))];
  return ids.length === 1 ? { firearmId: ids[0], buildId: null } : null;
}
